import React, { useEffect, useState, useCallback } from "react";
import { Button, Grid, FormControl, Select, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
// react datepicker
import DatePicker from "react-datepicker";
import { ko } from "date-fns/esm/locale";
import "react-datepicker/dist/react-datepicker.css";

import { useFormContext, Controller } from "react-hook-form";

import { ControlLabel, ButtonSearch, CustomDateInput } from "../index";
import {
  makeStartDate,
  makeEndDate,
  getDateFormat,
  addCommaToEvery3Digits,
} from "../../lib";
import { statusTotal, colorFinished, colorCanceledAllocation } from "../";
import SettlementService from "../../services/settlement";
import { useNavigate } from "react-router-dom";

const summaryCountFontSize = 15;
const summaryCountFontWeight = "bold";

const useStyles = makeStyles(() => ({
  filterContainer: {
    marginLeft: 2,
  },
  summaryContainer: {
    marginRight: 0,
  },

  label: {
    marginTop: 4,
  },
  select: {
    width: 68,
    marginLeft: 12,
  },
  date: {
    width: 115,
  },
  datePicker: {
    marginLeft: 7,
  },
  dateDelimiter: {
    marginLeft: 10,
    marginRight: 10,
  },

  summaryListFirst: {
    width: 100,
    marginTop: 0,
    marginBottom: 0,
    listStyle: "none",
    textAlign: "center",
    padding: 0,
  },
  summaryList: {
    width: 100,
    marginTop: 0,
    marginBottom: 0,
    listStyle: "none",
    textAlign: "center",
    padding: 0,
    borderLeft: "solid 1px #edf0f5",
  },
  summaryNumber: {
    fontSize: summaryCountFontSize,
    fontWeight: summaryCountFontWeight,
  },
  summaryFinished: {
    color: colorFinished,
  },
  summaryCanceledAllocation: {
    color: colorCanceledAllocation,
  },
  summaryTotal: {
    color: "#51c0ff",
  },
  button: {
    display: "inline-block",
    minWidth: 55,
    minHeight: 28,
    textAlign: "center",
    fontSize: 11,
  },
}));

const threeMonthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);

  return d;
};

const SelectStatus = () => {
  const classes = useStyles();
  const [status, setStatus] = useState<number>(statusTotal);

  const { control, setValue } = useFormContext();

  return (
    <Grid container direction={"row"}>
      <Grid item className={classes.label}>
        <ControlLabel name={"상태"} />
      </Grid>
      <Grid item>
        <Controller
          control={control}
          name={"status"}
          defaultValue={status}
          render={({ field }) => (
            <FormControl className={classes.select}>
              <Select
                {...field}
                defaultValue={status}
                variant={"standard"}
                onChange={(e: any) => {
                  field.onChange(e);
                  setValue("status", e.target.value);
                  setStatus(e.target.value);
                }}
              >
                <MenuItem value={statusTotal}>전체</MenuItem>
                <MenuItem value={2}>운송완료</MenuItem>
                <MenuItem value={3}>정산완료</MenuItem>
                <MenuItem value={11}>배차취소</MenuItem>
              </Select>
            </FormControl>
          )}
        />
      </Grid>
    </Grid>
  );
};

const SelectDate = () => {
  const classes = useStyles();
  const { control } = useFormContext();
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());

  const dateFormat = getDateFormat();
  const CustomInput = CustomDateInput();

  useEffect(() => {
    startDate.setDate(0); // 지난 달 마지막 날로 셋팅
    startDate.setDate(1); // 지난 달 1일로 셋팅
    makeStartDate(startDate);

    endDate.setDate(0); // 지난 달 마지막 날로 셋팅
    makeEndDate(endDate);
  }, []);

  return (
    <Grid container direction={"row"}>
      <Grid item className={classes.label}>
        <ControlLabel name={"기간"} />
      </Grid>
      <Grid item>
        <Grid container direction={"row"} spacing={1}>
          <Grid item className={classes.datePicker}>
            <Controller
              control={control}
              name={"startDate"}
              defaultValue={startDate}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  locale={ko} // 언어설정 기본값은 영어
                  dateFormat={dateFormat} // 날짜 형식 설정
                  minDate={threeMonthAgo()} // 선택할 수 있는 최소 날짜값 지정
                  maxDate={new Date()} // 선택할 수 있는 최대 날짜값 지정
                  selected={field.value}
                  onChange={(date: Date) => {
                    const d = makeStartDate(date);
                    field.onChange(d);
                    setStartDate(d);
                  }}
                  customInput={<CustomInput />}
                />
              )}
            />
          </Grid>
          <Grid item className={classes.dateDelimiter}>
            ~
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name={"endDate"}
              defaultValue={endDate}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  locale={ko} // 언어설정 기본값은 영어
                  dateFormat={dateFormat} // 날짜 형식 설정
                  minDate={startDate} // 선택할 수 있는 최소 날짜값 지정
                  maxDate={new Date()} // 선택할 수 있는 최대 날짜값 지정
                  selected={field.value}
                  onChange={(date: Date) => {
                    const d = makeEndDate(date);
                    field.onChange(d);
                    setEndDate(d);
                  }}
                  customInput={<CustomInput />}
                />
              )}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const Summary = (props: any) => {
  const classes = useStyles();
  const { summary } = props;

  return (
    <Grid container direction={"row"}>
      <Grid item>
        <ul className={classes.summaryListFirst}>
          <li>
            <ControlLabel
              name={"운행완료"}
              className={classes.summaryFinished}
            />
          </li>
          <li className={classes.summaryNumber}>
            {summary && summary.finished}
          </li>
        </ul>
      </Grid>
      <Grid item>
        <ul className={classes.summaryList}>
          <li>
            <ControlLabel
              name={"배차취소"}
              className={classes.summaryCanceledAllocation}
            />
          </li>
          <li className={classes.summaryNumber}>
            {summary && summary.canceledAllocation}
          </li>
        </ul>
      </Grid>
      <Grid item>
        <ul className={classes.summaryList}>
          <li>
            <ControlLabel name={"총금액"} className={classes.summaryTotal} />
          </li>
          <li className={classes.summaryNumber}>
            {summary && addCommaToEvery3Digits(summary.total)}
          </li>
        </ul>
      </Grid>
    </Grid>
  );
};

const makeDate = (d: Date): string => {
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  let monthStr;

  if (month < 10) {
    monthStr = "0" + month.toString();
  } else {
    monthStr = month.toString();
  }

  const date = d.getDate();
  let dateStr;
  if (date < 10) {
    dateStr = "0" + date.toString();
  } else {
    dateStr = date.toString();
  }

  return `${year}${monthStr}${dateStr}`;
};

export default function Header(props: any) {
  const classes = useStyles();
  const { summary } = props;

  const navigate = useNavigate();
  const { getValues } = useFormContext();

  const download = useCallback(async () => {
    const data = getValues();
    const settlementService = new SettlementService(navigate);
    const response = await settlementService.download(data);

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");

    const startDate = makeDate(data.startDate);
    const endDate = makeDate(data.endDate);

    const filename = `정산_${startDate}-${endDate}.xlsx`;

    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
  }, [getValues]);

  return (
    <Grid
      container
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
      spacing={3}
    >
      <Grid item className={classes.filterContainer}>
        <Grid
          container
          direction={"row"}
          justifyContent={"flex-start"}
          spacing={4}
        >
          <Grid item>
            <SelectStatus />
          </Grid>
          <Grid item>
            <SelectDate />
          </Grid>
          <Grid item>
            <ButtonSearch>검색</ButtonSearch>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={download}
              className={classes.button}
            >
              다운로드
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item className={classes.summaryContainer}>
        <Summary summary={summary} />
      </Grid>
    </Grid>
  );
}
